import WarehouseMap, {
  DEFAULT_LABEL_SIZE,
  isValidWarehouseName,
} from "../../../classes/WarehouseMap";
import { db } from "../../../boot/firebase";
import { i18n } from "../../../boot/i18n";
import { LOCATIONS } from "../../../constants";
import { serialize } from "../../../util/warehouseSvg";
import { deleteDoc, doc, setDoc, writeBatch } from "firebase/firestore";

// A brand-new map starts as an empty canonical document — zones get drawn
// on the canvas afterwards.
const EMPTY_SVG = serialize({
  viewBox: { x: 0, y: 0, w: 100, h: 60 },
  labelSize: DEFAULT_LABEL_SIZE,
  floor: [],
  zones: [],
});

export const GET_WAREHOUSE_MAP = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: WarehouseMap,
    path: WarehouseMap.getPath(id),
    stateKey: "warehouseMaps",
    stateID: id,
    id,
  });
};

export const GET_WAREHOUSE_MAPS = ({ dispatch }, { where, limit } = {}) => {
  return dispatch("GET_COLLECTION", {
    converter: WarehouseMap,
    path: WarehouseMap.getPath(),
    stateKey: "warehouseMaps",
    where,
    limit,
  });
};

export const LISTEN_WAREHOUSE_MAPS = (
  { dispatch },
  { where, next, error, unlisten } = {}
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: WarehouseMap,
    path: WarehouseMap.getPath(),
    stateKey: "warehouseMaps",
    where,
    next,
    error,
    unlisten,
  });
};

// Save a warehouse map. The document id is the warehouse name (used as the
// `warehouse:zone` reference prefix), so it must be supplied explicitly for
// new maps rather than auto-generated.
export const SAVE_WAREHOUSE_MAP = async ({ commit, state }, { id, data }) => {
  const now = new Date();
  const user = state.user;

  const map = new WarehouseMap(id, data);

  if (!map.id) {
    throw "A warehouse map id (name) is required";
  }

  const isNew = !(map.id in state.warehouseMaps);
  // The id prefixes every `warehouse:zone` location, so a new one must be
  // splittable (existing maps keep whatever they were created with).
  if (isNew && (!isValidWarehouseName(map.id) || map.id !== map.id.trim())) {
    throw "invalidWarehouseName";
  }
  if (isNew) {
    map.data.created = now;
    map.data.createdBy = user.id;
  }
  map.data.modified = now;
  map.data.modifiedBy = user.id;

  await setDoc(doc(db, WarehouseMap.getPath(map.id)), map.toFirestore());

  commit("SET", { stateKey: "warehouseMaps", stateID: map.id, doc: map });

  return map;
};

// Activate or deactivate a map. Only active maps contribute their zones to the
// receiving/put-away pickers. Deactivating also clears the default flag, since
// a hidden map should never be the receiving default.
export const SET_WAREHOUSE_MAP_ACTIVE = async (
  { state, dispatch },
  { id, active }
) => {
  const map = state.warehouseMaps?.[id];
  if (!map) return;
  const data = { ...map.data, active: Boolean(active) };
  if (!active) {
    data.isDefault = false;
  }
  return dispatch("SAVE_WAREHOUSE_MAP", { id, data });
};

// Archive or unarchive a map. Archived maps drop out of both the editor list
// and the location pickers; archiving also deactivates and clears default.
export const SET_WAREHOUSE_MAP_ARCHIVED = async (
  { state, dispatch },
  { id, archived }
) => {
  const map = state.warehouseMaps?.[id];
  if (!map) return;
  const data = { ...map.data, archived: Boolean(archived) };
  if (archived) {
    data.active = false;
    data.isDefault = false;
  }
  return dispatch("SAVE_WAREHOUSE_MAP", { id, data });
};

// Mark one warehouse map as the default receiving map (which implies it is
// active), clearing the default flag on whichever map currently holds it.
// Only maps whose flags actually change are written.
export const SET_DEFAULT_WAREHOUSE_MAP = async ({ state, dispatch }, id) => {
  if (!id) return;
  await Promise.all(
    Object.values(state.warehouseMaps || {}).map((map) => {
      const isTarget = map.id === id;
      const nextDefault = isTarget;
      const nextActive = isTarget ? true : Boolean(map.data.active);
      if (
        Boolean(map.data.isDefault) === nextDefault &&
        Boolean(map.data.active) === nextActive
      ) {
        return null;
      }
      return dispatch("SAVE_WAREHOUSE_MAP", {
        id: map.id,
        data: { ...map.data, isDefault: nextDefault, active: nextActive },
      });
    })
  );
};

// Persist a new display order: `ids` in list order, each written
// `order: index` in a single batch. Only maps present in state are touched.
export const REORDER_WAREHOUSE_MAPS = async ({ state, commit }, ids) => {
  const now = new Date();
  const batch = writeBatch(db);
  const updated = [];
  (ids || []).forEach((id, index) => {
    const map = state.warehouseMaps?.[id];
    if (!map) return;
    const next = new WarehouseMap(id, { ...map.data, order: index });
    next.data.modified = now;
    next.data.modifiedBy = state.user.id;
    batch.set(doc(db, WarehouseMap.getPath(id)), next.toFirestore());
    updated.push(next);
  });
  await batch.commit();
  updated.forEach((map) =>
    commit("SET", { stateKey: "warehouseMaps", stateID: map.id, doc: map })
  );
  return updated;
};

// Ask for a name and create a blank, inactive map with it, sorted after the
// last existing map. `success` gets the new map's id once it is saved (the
// caller opens its canvas). Shared by the map list and the canvas's
// warehouse switcher.
export const PROMPT_NEW_WAREHOUSE_MAP = (
  { dispatch, state, getters },
  { color, success } = {}
) => {
  if (!WarehouseMap.canBeCreatedBy(state.user)) return;
  const maps = state.warehouseMaps || {};
  dispatch("PROMPT", {
    title: i18n.global.t("New warehouse map"),
    message: i18n.global.t("hint.warehouseName"),
    prompt: {
      model: "",
      isValid: (name) => {
        const id = (name || "").trim();
        return (
          isValidWarehouseName(id) &&
          !Object.keys(maps).some(
            (key) => key.toLowerCase() === id.toLowerCase()
          )
        );
      },
    },
    color,
    success: async (name) => {
      const id = name.trim();
      // Default to a location that already has a warehouse (any location
      // may be chosen afterwards in the map's settings).
      const locationId =
        LOCATIONS.find(
          ({ value }) => getters.warehousesForLocation?.(value)?.length
        )?.value ||
        LOCATIONS[0]?.value ||
        "";
      const order =
        Math.max(-1, ...Object.values(maps).map((map) => map.data.order || 0)) +
        1;
      try {
        await dispatch("SAVE_WAREHOUSE_MAP", {
          id,
          data: {
            name: id,
            locationId,
            order,
            labelSize: DEFAULT_LABEL_SIZE,
            gridSize: 1,
            active: false,
            isDefault: false,
            archived: false,
            svg: EMPTY_SVG,
            zones: {},
          },
        });
      } catch (error) {
        dispatch("NOTIFY_ERROR", error);
        return;
      }
      dispatch("NOTIFY_SUCCESS", "warehouseMapSaved");
      if (success) success(id);
    },
  });
};

export const DELETE_WAREHOUSE_MAP = async ({ commit }, id) => {
  if (!id) return;
  await deleteDoc(doc(db, WarehouseMap.getPath(id)));
  commit("REMOVE", { stateKey: "warehouseMaps", stateID: id });
};
